import React, { useState, useEffect } from 'react';
import {
  Wallet,
  Plus,
  Edit,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Search,
  RefreshCw,
  Check,
  X,
  AlertCircle,
  Copy,
  CheckCircle,
  TrendingUp,
  Activity,
  DollarSign,
  Eye,
  Clock,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import EnhancedDepositMonitoring from './EnhancedDepositMonitoring';

const CryptoAddressManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [stats, setStats] = useState(null);
  const [depositMonitoring, setDepositMonitoring] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [editingWallet, setEditingWallet] = useState(null);
  const [selectedAddressDeposits, setSelectedAddressDeposits] = useState(null);
  const [activeTab, setActiveTab] = useState('addresses'); // 'addresses' or 'monitoring'
  const [formData, setFormData] = useState({
    currency: 'btc',
    network: 'BTC',
    address: '',
    qr_code: '',
    is_active: true,
    display_order: 0,
    notes: ''
  });

  const BACKEND_URL = '';

  const SUPPORTED_CURRENCIES = {
    btc: { name: 'Bitcoin', networks: ['BTC'] },
    eth: { name: 'Ethereum', networks: ['ERC20'] },
    usdt: { name: 'Tether USD', networks: ['ERC20', 'TRC20', 'BEP20'] },
    usdc: { name: 'USD Coin', networks: ['ERC20', 'BEP20'] },
    bnb: { name: 'Binance Coin', networks: ['BEP20', 'BSC'] },
    sol: { name: 'Solana', networks: ['SOL'] },
    xrp: { name: 'XRP', networks: ['XRP'] },
    ada: { name: 'Cardano', networks: ['ADA'] },
    doge: { name: 'Dogecoin', networks: ['DOGE'] },
    trx: { name: 'TRON', networks: ['TRC20'] },
    ltc: { name: 'Litecoin', networks: ['LTC'] },
    matic: { name: 'Polygon', networks: ['MATIC', 'Polygon'] }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      
      const [walletsRes, statsRes, monitoringRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/crypto-addresses/list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/admin/crypto-addresses/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/admin/crypto-addresses/deposit-monitor`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (walletsRes.ok) {
        const data = await walletsRes.json();
        setWallets(data.wallets || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (monitoringRes.ok) {
        const data = await monitoringRes.json();
        setDepositMonitoring(data.data || null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const viewAddressDeposits = async (wallet) => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/crypto-addresses/deposits-by-address/${wallet.currency}/${wallet.network}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setSelectedAddressDeposits(data);
        setShowDepositModal(true);
      }
    } catch (error) {
      console.error('Error fetching deposits:', error);
      toast({
        title: 'Error',
        description: 'Failed to load deposits',
        variant: 'destructive'
      });
    }
  };

  const filteredWallets = wallets.filter(wallet =>
    wallet.currency.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wallet.network.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wallet.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: 'Address copied to clipboard'
    });
  };

  const handleToggleActive = async (wallet) => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/crypto-addresses/toggle/${wallet.currency}/${wallet.network}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        toast({
          title: 'Success',
          description: `Address ${wallet.is_active ? 'deactivated' : 'activated'} successfully`
        });
        fetchData(); // Refresh the list
      } else {
        throw new Error('Failed to toggle address');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle address status',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteAddress = async (wallet) => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/crypto-addresses/delete/${wallet.currency}/${wallet.network}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Address deleted successfully'
        });
        fetchData(); // Refresh the list
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete address');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete address',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Wallet className="w-8 h-8 text-blue-600" />
          Crypto Address Management
        </h1>
        <p className="text-gray-600 mt-2">Manage cryptocurrency deposit addresses and monitor deposits</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('addresses')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'addresses'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Wallet className="w-5 h-5 inline mr-2" />
          Addresses
        </button>
        <button
          onClick={() => setActiveTab('monitoring')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'monitoring'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Activity className="w-5 h-5 inline mr-2" />
          Deposit Monitoring
        </button>
      </div>

      {/* Addresses Tab */}
      {activeTab === 'addresses' && (
        <div>
          {/* Statistics Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Addresses</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_addresses}</p>
                  </div>
                  <Wallet className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.active_addresses}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Inactive</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.inactive_addresses}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Currencies</p>
                    <p className="text-2xl font-bold text-gray-900">{Object.keys(stats.by_currency || {}).length}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-purple-600" />
                </div>
              </div>
            </div>
          )}

          {/* Search and Actions */}
          <div className="flex justify-between items-center mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by currency, network, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Address
              </button>
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Wallets Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Network
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mb-2" />
                        <p className="text-gray-500">Loading addresses...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredWallets.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <Wallet className="w-12 h-12 text-gray-400 mb-3" />
                        <p className="text-gray-600 font-medium mb-1">No crypto addresses found</p>
                        <p className="text-gray-500 text-sm mb-4">
                          {searchTerm ? 'Try adjusting your search' : 'Get started by adding your first address'}
                        </p>
                        {!searchTerm && (
                          <button
                            onClick={() => setShowAddModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            Add First Address
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredWallets.map((wallet) => (
                  <tr key={`${wallet.currency}-${wallet.network}-${wallet.id || wallet.address}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 uppercase">{wallet.currency}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {wallet.network}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 font-mono">
                          {wallet.address.substring(0, 20)}...{wallet.address.substring(wallet.address.length - 10)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(wallet.address)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          <Copy className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {wallet.is_active ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded flex items-center gap-1 w-fit">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded flex items-center gap-1 w-fit">
                          <X className="w-3 h-3" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => viewAddressDeposits(wallet)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="View Deposits"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingWallet(wallet);
                            setShowEditModal(true);
                          }}
                          className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Edit Address"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(wallet)}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                          title={wallet.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {wallet.is_active ? (
                            <ToggleRight className="w-4 h-4" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete ${(wallet.currency || '').toUpperCase()} address on ${wallet.network}?`)) {
                              handleDeleteAddress(wallet);
                            }
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete Address"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monitoring Tab - Enhanced */}
      {activeTab === 'monitoring' && (
        <EnhancedDepositMonitoring />
      )}

      {/* Legacy Monitoring Tab (kept for reference, hidden) */}
      {false && activeTab === 'monitoring-legacy' && depositMonitoring && (
        <div>
          {/* Monitoring Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Deposits</p>
                  <p className="text-2xl font-bold text-gray-900">{depositMonitoring.total_deposits}</p>
                </div>
                <Activity className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">{depositMonitoring.pending_deposits}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Confirmed</p>
                  <p className="text-2xl font-bold text-gray-900">{depositMonitoring.confirmed_deposits}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${depositMonitoring.total_deposit_amount.toLocaleString()}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Deposits by Currency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Deposits by Currency</h3>
              <div className="space-y-3">
                {Object.entries(depositMonitoring.by_currency).map(([currency, data]) => (
                  <div key={currency} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium text-gray-900 uppercase">{currency}</p>
                      <p className="text-sm text-gray-600">
                        {data.confirmed} confirmed | {data.pending} pending
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">${data.amount.toLocaleString()}</p>
                      <p className="text-sm text-gray-600">{data.total} deposits</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Addresses by Usage */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Top Addresses by Usage</h3>
              <div className="space-y-3">
                {depositMonitoring.top_addresses.map((addr, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 uppercase">
                        {(addr.currency || '').toUpperCase()} - {addr.network}
                      </p>
                      <p className="text-sm text-gray-600 font-mono truncate">
                        {addr.address.substring(0, 15)}...{addr.address.substring(addr.address.length - 8)}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-semibold text-gray-900">{addr.total_deposits}</p>
                      <p className="text-sm text-gray-600">deposits</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Deposits */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Recent Deposits (Last 10)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {depositMonitoring.recent_deposits.map((deposit) => (
                    <tr key={deposit.deposit_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {deposit.user_email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          {(deposit.currency || '').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {deposit.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          deposit.status === 'confirmed'
                            ? 'bg-green-100 text-green-800'
                            : deposit.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {deposit.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {new Date(deposit.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Deposit Details Modal */}
      {showDepositModal && selectedAddressDeposits && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Deposits for {(selectedAddressDeposits.currency || '').toUpperCase()} - {selectedAddressDeposits.network}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1 font-mono">
                    {selectedAddressDeposits.address}
                  </p>
                </div>
                <button
                  onClick={() => setShowDepositModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Statistics */}
            <div className="p-6 border-b border-gray-200">
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedAddressDeposits.statistics.total_deposits}
                  </p>
                  <p className="text-sm text-gray-600">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">
                    {selectedAddressDeposits.statistics.pending}
                  </p>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {selectedAddressDeposits.statistics.confirmed}
                  </p>
                  <p className="text-sm text-gray-600">Confirmed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    ${selectedAddressDeposits.statistics.total_amount.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600">Amount</p>
                </div>
              </div>
            </div>

            {/* Deposits List */}
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Deposit History</h3>
              <div className="space-y-3">
                {selectedAddressDeposits.deposits.map((deposit) => (
                  <div
                    key={deposit.deposit_id}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{deposit.user_email}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Amount: <span className="font-semibold">{deposit.amount}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          Date: {new Date(deposit.created_at).toLocaleString()}
                        </p>
                      </div>
                      <span className={`px-3 py-1 text-sm font-medium rounded ${
                        deposit.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : deposit.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {deposit.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CryptoAddressManagement;